import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  SimpleChange,
} from '@angular/core';
import { changeBodyAttribute, getLayoutConfig } from '../shared/helper/utils';
import { EventService } from 'src/app/core/services/event.service';
import { EventType } from 'src/app/core/constants/events';
import { LayoutType } from '../shared/config/layout.model';
import { UsersService } from 'src/app/core/services/users.service';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { Router } from '@angular/router';
import { AlertService } from 'src/app/core/services/alert.service';
import { UserConfigPollingService } from 'src/app/core/services/user-config-polling.service';
import { Subscription } from 'rxjs';
import { ViewChild, TemplateRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-private-layout',
  templateUrl: './private-layout.component.html',
  styleUrls: ['./private-layout.component.scss'],
})
export class PrivateLayoutComponent implements OnInit, OnDestroy {
  @Input() layoutColor: string = '';
  @Input() layoutWidth: string = '';
  leftbarPosition: string = '';
  leftbarColor: string = '';
  leftbarSize: string = '';
  topbarColor: string = '';
  showSidebarUserInfo: boolean = false;
  reRender: boolean = true;
  @ViewChild('capabilitiesUpdateModal', { static: true })
  capabilitiesUpdateModalTemplate!: TemplateRef<any>;
  @ViewChild('tenantsUpdateModal', { static: true })
  tenantsUpdateModalTemplate!: TemplateRef<any>;
  private capabilitiesPollingSubscription?: Subscription;
  private tenantsPollingSubscription?: Subscription;
  private capabilitiesUpdateModalRef: any = null;
  private tenantsUpdateModalRef: any = null;

  constructor(
    private eventService: EventService,
    private usersService: UsersService,
    private authService: AuthenticationService,
    private router: Router,
    private alertService: AlertService,
    private userConfigPollingService: UserConfigPollingService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    let config = getLayoutConfig(LayoutType.LAYOUT_VERTICAL);
    this.leftbarPosition = config.leftbarPosition;
    this.leftbarColor = config.leftbarColor;
    this.leftbarSize = config.leftbarSize;
    this.topbarColor = config.topbarColor;
    this.showSidebarUserInfo = config.showSidebarUserInfo;

    this.eventService
      .on(EventType.CHANGE_LEFTBAR_POSITION)
      .subscribe(({ payload }) => {
        this.leftbarPosition = payload.toString();
      });

    this.eventService
      .on(EventType.CHANGE_LEFTBAR_COLOR)
      .subscribe(({ payload }) => {
        this.leftbarColor = payload.toString();
      });

    this.eventService
      .on(EventType.CHANGE_LEFTBAR_SIZE)
      .subscribe(({ payload }) => {
        this.leftbarSize = payload.toString();
      });

    this.eventService
      .on(EventType.CHANGE_TOPBAR_COLOR)
      .subscribe(({ payload }) => {
        this.topbarColor = payload.toString();
      });

    this.eventService
      .on(EventType.TOGGLE_SIDEBAR_USERINFO)
      .subscribe(({ payload }) => {
        this.showSidebarUserInfo = Boolean(payload);
      });
    this.changeLayoutConfig();

    this.checkAndUpdateUserCapabilities();

    this.eventService.on(EventType.USER_CAPABILITIES_UPDATED).subscribe(() => {
      const currentUser = this.authService.currentUser();
      if (currentUser && currentUser.capabilities) {
        this.checkCurrentRoutePermissions(currentUser.capabilities);
      }
    });

    this.userConfigPollingService.startPolling();

    this.capabilitiesPollingSubscription = this.userConfigPollingService
      .onCapabilitiesChanged()
      .subscribe(() => {
        this.showCapabilitiesUpdateModal();
      });

    // Suscribirse a cambios de tenants detectados por el polling
    this.tenantsPollingSubscription = this.userConfigPollingService
      .onTenantsChanged()
      .subscribe(() => {
        this.showTenantsUpdateModal();
      });
  }

  ngAfterViewInit() {
    changeBodyAttribute('data-layout-mode', LayoutType.LAYOUT_VERTICAL);
  }

  ngOnChanges(changes: SimpleChange) {
    this._setRerender();
    this.changeLayoutConfig();
  }

  ngDoCheck(): void {
    this.changeLayoutConfig();
  }

  ngOnDestroy(): void {
    changeBodyAttribute('data-layout-mode', '', 'remove');
    this.userConfigPollingService.stopPolling();

    if (this.capabilitiesPollingSubscription) {
      this.capabilitiesPollingSubscription.unsubscribe();
    }

    if (this.tenantsPollingSubscription) {
      this.tenantsPollingSubscription.unsubscribe();
    }
  }

  _setRerender = () => {
    this.reRender = false;
    setTimeout(() => {
      this.reRender = true;
    }, 20);
  };

  changeLayoutConfig(): void {
    // modo claro/oscuro
    changeBodyAttribute('data-layout-color', this.layoutColor);

    // ancho fijo/fluido
    changeBodyAttribute('data-layout-size', this.layoutWidth);

    // posición del menú lateral
    changeBodyAttribute('data-leftbar-position', this.leftbarPosition);

    // tema del menú lateral
    changeBodyAttribute('data-leftbar-color', this.leftbarColor);

    // tipo de menú lateral
    changeBodyAttribute('data-leftbar-size', this.leftbarSize);

    // tema de la barra superior
    changeBodyAttribute('data-topbar-color', this.topbarColor);
  }

  /**
   * Mostrar/ocultar menú lateral en móvil
   */
  onToggleMobileMenu() {
    document.body.classList.toggle('sidebar-enable');
  }

  /**
   * Verificar si las capacidades del usuario o el tenant seleccionado han cambiado en el servidor
   * y actualizarlas si es necesario
   */
  private checkAndUpdateUserCapabilities(): void {
    const currentUser = this.authService.currentUser();
    const token = this.authService.getToken();

    if (currentUser && token) {
      this.usersService.getUserData(token).subscribe(
        (response: any) => {
          if (response.data) {
            const serverCapabilities = response.data.capabilities || [];
            const currentCapabilities = currentUser.capabilities || [];

            const normalizeCapabilities = (caps: any[]) => {
              return caps
                .map((cap: any) => {
                  if (typeof cap === 'object' && cap.key) {
                    return cap.key;
                  }
                  return cap;
                })
                .sort();
            };

            const serverCapsNormalized =
              normalizeCapabilities(serverCapabilities);
            const currentCapsNormalized =
              normalizeCapabilities(currentCapabilities);

            const capabilitiesChanged =
              JSON.stringify(serverCapsNormalized) !==
              JSON.stringify(currentCapsNormalized);

            // Verificar si el tenant seleccionado ha cambiado
            const serverSelectedTenant = response.data.providers?.find(
              (provider: any) => provider.selected === true
            )?.id;
            const currentSelectedTenant = currentUser.providers?.find(
              (provider: any) => provider.selected === true
            )?.id;

            const tenantChanged =
              serverSelectedTenant !== undefined &&
              serverSelectedTenant !== currentSelectedTenant;

            // Actualizar si las capacidades o el tenant han cambiado
            if (capabilitiesChanged || tenantChanged) {
              const userData = {
                id: response.data.id,
                email: response.data.email,
                active: response.data.active,
                name: response.data.name,
                surname: response.data.surname,
                phone: response.data.phone,
                capabilities: response.data.capabilities,
                token: token,
                notificationPush: response.data.notificationPush,
                notificationEmail: response.data.notificationEmail,
                providers: response.data.providers,
                isAdmin: response.data.isAdmin,
              };

              sessionStorage.setItem('cayc-user', JSON.stringify(userData));
              this.authService.clearUserCache();

              if (capabilitiesChanged) {
                this.checkCurrentRoutePermissions(response.data.capabilities);
                this.eventService.broadcast(
                  EventType.USER_CAPABILITIES_UPDATED,
                  true
                );
              }

              // Si el tenant cambió, recargar la página para reflejar el cambio
              if (tenantChanged) {
                window.location.reload();
              }
            }
          }
        },
        (error: any) => {
          console.warn(
            'No se pudo verificar las capacidades del usuario:',
            error
          );
        }
      );
    }
  }

  /**
   * Verificar si el usuario tiene permisos para la ruta actual
   * Si no los tiene, redirigir al dashboard
   * @param newCapabilities Nuevas capacidades del usuario
   */
  private checkCurrentRoutePermissions(newCapabilities: any[]): void {
    const currentUrl = this.router.url;

    const routeCapabilitiesMap: { [key: string]: string[] } = {
      '/upload': ['LEASING'],
      '/lease': ['LEASING'],
      '/search': ['QUERY'],
      '/listing': ['LEASING'],
      '/users': ['CONFIG'],
      '/certificates': ['CONFIG'],
      '/settings': ['CONFIG'],
      '/log': ['CONFIG'],
    };

    const requiredCapabilities = routeCapabilitiesMap[currentUrl];

    if (requiredCapabilities && requiredCapabilities.length > 0) {
      const userCapabilities = (newCapabilities || []).map((cap: any) => {
        if (typeof cap === 'object' && cap.key) {
          return cap.key;
        }
        return cap;
      });

      const hasRequiredCapability = requiredCapabilities.some((requiredCap) =>
        userCapabilities.includes(requiredCap)
      );

      if (!hasRequiredCapability) {
        this.alertService.showAlert({
          type: 'danger',
          message: 'Acceso no autorizado',
          show: true,
        });
        this.router.navigate(['/dashboard']);
      }
    }
  }

  /**
   * Mostrar modal de actualización de capacidades
   */
  private showCapabilitiesUpdateModal(): void {
    if (!this.capabilitiesUpdateModalRef) {
      this.capabilitiesUpdateModalRef = this.modalService.open(
        this.capabilitiesUpdateModalTemplate,
        {
          size: 'md',
          modalDialogClass: 'modal-filled bg-warning',
          backdrop: 'static',
          keyboard: false,
        }
      );
    }
  }

  /**
   * Mostrar modal de actualización de tenants
   */
  private showTenantsUpdateModal(): void {
    if (!this.tenantsUpdateModalRef) {
      this.tenantsUpdateModalRef = this.modalService.open(
        this.tenantsUpdateModalTemplate,
        {
          size: 'md',
          modalDialogClass: 'modal-filled bg-warning',
          backdrop: 'static',
          keyboard: false,
        }
      );
    }
  }

  /**
   * Aceptar y recargar la página (para modal de capacidades)
   */
  acceptAndReload(): void {
    if (this.capabilitiesUpdateModalRef) {
      this.capabilitiesUpdateModalRef.close();
      this.capabilitiesUpdateModalRef = null;
    }
    window.location.reload();
  }

  /**
   * Aceptar y recargar la página (para modal de tenants)
   */
  acceptAndReloadTenants(): void {
    if (this.tenantsUpdateModalRef) {
      this.tenantsUpdateModalRef.close();
      this.tenantsUpdateModalRef = null;
    }
    window.location.reload();
  }
}
